import React from 'react';
import { Box, Typography, Stack } from 'omaha-design-system';
import type { PlayerRotation } from './types';
import { TEAM_COLORS, ROTATION_COLORS } from './constants';
import PlayerRow from './PlayerRow';

interface TeamSectionProps {
  players: PlayerRotation[];
  opponentPlayers: PlayerRotation[];
  teamAbbrev: string;
  opponentAbbrev: string;
  teamScore: number;
  gameDuration: number;
  isHome: boolean;
  selectedPlayerNames: Set<string>;
  onPlayerSelect: (player: PlayerRotation, teamAbbrev: string, e: React.MouseEvent) => void;
}

const TeamSection: React.FC<TeamSectionProps> = ({
  players,
  opponentPlayers,
  teamAbbrev,
  opponentAbbrev,
  teamScore,
  gameDuration,
  isHome,
  selectedPlayerNames,
  onPlayerSelect,
}) => {
  const teamColor = TEAM_COLORS[teamAbbrev] || '#6B7280';

  return (
    <Box sx={{ mb: 1 }}>
      <Stack
        direction="row"
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          py: 0.75,
          px: 1,
          backgroundColor: ROTATION_COLORS.bg.card,
          borderRadius: '4px 4px 0 0',
          borderLeft: '2px solid',
        }}
        style={{ borderLeftColor: teamColor }}
      >
        <Typography sx={{ fontSize: '0.9rem', fontWeight: 700 }} style={{ color: teamColor }}>
          {teamAbbrev}
        </Typography>
        <Typography sx={{ fontSize: '0.9rem', color: ROTATION_COLORS.text.secondary }}>{teamScore}</Typography>
        <Typography
          component="span"
          sx={{
            fontSize: '0.6rem',
            color: ROTATION_COLORS.text.muted,
            ml: 'auto',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
          }}
        >
          {isHome ? 'Home' : 'Away'}
        </Typography>
      </Stack>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          height: 32,
          fontSize: '0.7rem',
          color: ROTATION_COLORS.text.muted,
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          borderBottom: `1px solid ${ROTATION_COLORS.border.subtle}`,
        }}
      >
        <Typography component="div" sx={{ width: 140, pl: 1, pr: 1 }}>Player</Typography>
        <Typography component="div" sx={{ textAlign: 'center', fontSize: '0.7rem', width: 40 }}>MIN</Typography>
        <Typography component="div" sx={{ textAlign: 'center', fontSize: '0.7rem', width: 40 }}>PTS</Typography>
        <Typography component="div" sx={{ textAlign: 'center', fontSize: '0.7rem', width: 36 }}>REB</Typography>
        <Typography component="div" sx={{ textAlign: 'center', fontSize: '0.7rem', width: 36 }}>AST</Typography>
        <Typography component="div" sx={{ textAlign: 'center', fontSize: '0.7rem', width: 36 }}>S+B</Typography>
        <Typography component="div" sx={{ textAlign: 'center', fontSize: '0.7rem', width: 36 }}>PF</Typography>
        <Typography component="div" sx={{ flex: 1, textAlign: 'center' }}>Rotation</Typography>
        <Typography component="div" sx={{ width: 48, textAlign: 'right', pr: 1 }}>+/-</Typography>
      </Box>

      <Box sx={{ backgroundColor: ROTATION_COLORS.bg.deep, borderRadius: '0 0 4px 4px' }}>
        {players.map((player, idx) => (
          <PlayerRow
            key={`${player.playerId}-${idx}`}
            player={player}
            gameDuration={gameDuration}
            allPlayers={players}
            opponentPlayers={opponentPlayers}
            isSelected={selectedPlayerNames.has(player.playerName)}
            onSelect={(p, e) => onPlayerSelect(p, teamAbbrev, e)}
            teamAbbrev={teamAbbrev}
            opponentAbbrev={opponentAbbrev}
            index={idx}
          />
        ))}
      </Box>
    </Box>
  );
};

export default TeamSection;
